import api from "../api";

export interface PdfOptionSchemaProperty {
  type?: "string" | "boolean" | "integer" | "number";
  title?: string;
  description?: string;
  default?: unknown;
  format?: string;
  writeOnly?: boolean;
  minLength?: number;
}

/** JSON schema of a splitter's Pydantic options model (the job form). */
export interface PdfOptionsSchema {
  properties?: Record<string, PdfOptionSchemaProperty>;
  required?: string[];
}

export interface PdfSplitterInfo {
  key: string;
  label: string;
  input_kinds: string[];
  options_schema: PdfOptionsSchema;
}

export interface PdfToolsConfig {
  splitters: PdfSplitterInfo[];
  limits: {
    max_file_bytes: number;
    max_files_per_job: number;
    max_total_bytes: number;
  };
}

export type PdfJobStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "stopped"
  | "canceled";

export type PdfJobResultState = "" | "in_review" | "saving" | "saved" | "discarded";

export type PdfPublishStatus =
  | "none"
  | "pending"
  | "running"
  | "completed"
  | "failed";

export interface PdfJobBase {
  job_id: string;
  status: PdfJobStatus;
  result_state: PdfJobResultState;
  splitter_key: string;
  splitter_label: string;
  user: { id: number | null; username: string };
  created_by: { id: number | null; username: string };
  files_count: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface PdfJobListItem extends PdfJobBase {
  error_pages: number;
}

export interface PdfJobSummaryTotals {
  files?: number;
  pages?: number;
  renamed?: number;
  error_pages?: number;
  skipped?: number;
  aborted_files?: number;
  employee_folders?: number;
}

export interface PdfJobFileSummary {
  file_name: string;
  pages: number;
  renamed: number;
  error_pages: number;
  skipped: number;
  aborted: boolean;
  abort_reason: string | null;
}

export interface PdfJobDetail extends PdfJobBase {
  options: Record<string, unknown>;
  inputs: { file_name: string; size: number | null; kind: string }[];
  progress: {
    phase: string;
    current_file: string;
    note: string;
    total_files: number;
    processed_files: number;
    total_pages: number;
    processed_pages: number;
  };
  summary: {
    totals: PdfJobSummaryTotals;
    per_file: PdfJobFileSummary[];
  } | null;
  error_message: string;
  publish: {
    status: PdfPublishStatus;
    destination: string;
    user_folder_id: number | null;
    error: string;
    published_at: string | null;
  };
  discarded_at: string | null;
  has_results_zip: boolean;
}

export interface PdfJobListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PdfJobListItem[];
}

export interface PdfUploadPresignResponse {
  upload_url: string;
  s3_key: string;
  s3_bucket: string;
  file_name: string;
  headers: Record<string, string>;
}

export interface PdfUploadFinalizeResponse {
  s3_key: string;
  file_name: string;
  size: number;
  kind: "zip" | "pdf";
}

export interface CreatePdfJobRequest {
  user_id: number;
  splitter_key: string;
  options: Record<string, unknown>;
  inputs: { s3_key: string; file_name: string }[];
}

export type PdfWorkspaceSection = "input" | "result" | "error_pages";

export interface PdfWorkspaceItem {
  type: "folder" | "file";
  name: string;
  path: string;
  key?: string;
  size?: number;
}

export interface PdfWorkspaceListing {
  section: PdfWorkspaceSection;
  prefix: string;
  items: PdfWorkspaceItem[];
  next_cursor: string | null;
}

export interface PdfJobLogEvent {
  timestamp: number | null;
  message: string;
}

export interface PdfJobLogsResponse {
  source: "cloudwatch" | "file";
  events: PdfJobLogEvent[];
  next_token: string | null;
}

const BASE = "/pdf-tools";

export const pdfToolsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPdfToolsConfig: builder.query<PdfToolsConfig, void>({
      query: () => ({ url: `${BASE}/config/` }),
      keepUnusedDataFor: 3600,
    }),

    presignPdfUpload: builder.mutation<
      PdfUploadPresignResponse,
      { file_name: string; content_type: string; user_id: number }
    >({
      query: (body) => ({
        url: `${BASE}/uploads/presign/`,
        method: "POST",
        body,
      }),
    }),

    finalizePdfUpload: builder.mutation<
      PdfUploadFinalizeResponse,
      { s3_key: string; user_id: number }
    >({
      query: (body) => ({
        url: `${BASE}/uploads/finalize/`,
        method: "POST",
        body,
      }),
    }),

    createPdfJob: builder.mutation<PdfJobDetail, CreatePdfJobRequest>({
      query: (body) => ({
        url: `${BASE}/jobs/`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "PdfSplitJobs", id: "LIST" }],
    }),

    getPdfJobs: builder.query<
      PdfJobListResponse,
      { page?: number; page_size?: number; user_id?: number | null; status?: string | null }
    >({
      query: ({ page, page_size, user_id, status }) => ({
        url: `${BASE}/jobs/`,
        params: {
          page: page ?? 1,
          page_size: page_size ?? 25,
          user_id: user_id || undefined,
          status: status || undefined,
        },
      }),
      providesTags: [{ type: "PdfSplitJobs", id: "LIST" }],
    }),

    getPdfJob: builder.query<PdfJobDetail, string>({
      query: (jobId) => ({ url: `${BASE}/jobs/${jobId}/` }),
      providesTags: (_r, _e, jobId) => [{ type: "PdfSplitJobs", id: jobId }],
    }),

    getPdfJobFiles: builder.query<
      PdfWorkspaceListing,
      {
        jobId: string;
        section: PdfWorkspaceSection;
        prefix?: string;
        cursor?: string | null;
        limit?: number;
      }
    >({
      query: ({ jobId, section, prefix, cursor, limit }) => ({
        url: `${BASE}/jobs/${jobId}/files/`,
        params: {
          section,
          prefix: prefix || undefined,
          cursor: cursor || undefined,
          limit: limit ?? 100,
        },
      }),
    }),

    getPdfJobFileUrl: builder.mutation<
      { url: string; file_name: string },
      { jobId: string; key: string }
    >({
      query: ({ jobId, key }) => ({
        url: `${BASE}/jobs/${jobId}/file/`,
        params: { key },
      }),
    }),

    getPdfJobLogs: builder.query<
      PdfJobLogsResponse,
      { jobId: string; next_token?: string | null }
    >({
      query: ({ jobId, next_token }) => ({
        url: `${BASE}/jobs/${jobId}/logs/`,
        params: { next_token: next_token || undefined },
      }),
    }),

    getPdfJobDownload: builder.query<
      { download_url: string; filename: string },
      string
    >({
      query: (jobId) => ({ url: `${BASE}/jobs/${jobId}/download/` }),
    }),

    savePdfJob: builder.mutation<
      PdfJobDetail,
      {
        jobId: string;
        user_folder_id: number;
        dest_subpath: string;
        overwrite?: boolean;
      }
    >({
      query: ({ jobId, ...body }) => ({
        url: `${BASE}/jobs/${jobId}/save/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { jobId }) => [
        { type: "PdfSplitJobs", id: jobId },
        { type: "PdfSplitJobs", id: "LIST" },
      ],
    }),

    discardPdfJob: builder.mutation<
      PdfJobDetail,
      { jobId: string; delete_data: boolean; delete_saved_data: boolean }
    >({
      query: ({ jobId, delete_data, delete_saved_data }) => ({
        url: `${BASE}/jobs/${jobId}/discard/`,
        method: "POST",
        body: { delete_data, delete_saved_data },
      }),
      invalidatesTags: (_r, _e, { jobId }) => [
        { type: "PdfSplitJobs", id: jobId },
        { type: "PdfSplitJobs", id: "LIST" },
      ],
    }),

    stopPdfJob: builder.mutation<PdfJobDetail, string>({
      query: (jobId) => ({
        url: `${BASE}/jobs/${jobId}/stop/`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, jobId) => [
        { type: "PdfSplitJobs", id: jobId },
        { type: "PdfSplitJobs", id: "LIST" },
      ],
    }),

    restartPdfJob: builder.mutation<
      PdfJobDetail,
      { jobId: string; mode: "resume" | "fresh" }
    >({
      query: ({ jobId, mode }) => ({
        url: `${BASE}/jobs/${jobId}/restart/`,
        method: "POST",
        body: { mode },
      }),
      invalidatesTags: (_r, _e, { jobId }) => [
        { type: "PdfSplitJobs", id: jobId },
        { type: "PdfSplitJobs", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPdfToolsConfigQuery,
  usePresignPdfUploadMutation,
  useFinalizePdfUploadMutation,
  useCreatePdfJobMutation,
  useGetPdfJobsQuery,
  useGetPdfJobQuery,
  useLazyGetPdfJobQuery,
  useGetPdfJobFilesQuery,
  useLazyGetPdfJobFilesQuery,
  useGetPdfJobFileUrlMutation,
  useGetPdfJobLogsQuery,
  useLazyGetPdfJobLogsQuery,
  useLazyGetPdfJobDownloadQuery,
  useSavePdfJobMutation,
  useDiscardPdfJobMutation,
  useStopPdfJobMutation,
  useRestartPdfJobMutation,
} = pdfToolsApi;
